const DEFAULT_LIMIT = 3;

const normalizeTankReference = (value) =>
  String(value || "")
    .trim()
    .toUpperCase();

const getTimestampValue = (value) => {
  if (!value) return 0;

  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const compareByNewestCompletedAt = (left, right) =>
  getTimestampValue(right.completedAt) - getTimestampValue(left.completedAt);

const sortTanksByName = (left, right) => {
  const leftName = String(left?.tankName || "");
  const rightName = String(right?.tankName || "");

  const leftNumber = parseInt(leftName.match(/\d+/)?.[0] || "0", 10);
  const rightNumber = parseInt(rightName.match(/\d+/)?.[0] || "0", 10);

  if (leftNumber === rightNumber) {
    if (leftName.includes("P") && rightName.includes("S")) return -1;
    if (leftName.includes("S") && rightName.includes("P")) return 1;
  }

  return leftName.localeCompare(rightName, undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

const getTankRowsFromFallback = (configuredTanks = []) =>
  configuredTanks.map((tank) => ({
    tankId: tank.id,
    tankName: tank.name,
    aliases: [
      normalizeTankReference(tank.id),
      normalizeTankReference(tank.name),
    ].filter(Boolean),
  }));

const getTankRowsFromEntities = (tanks = []) =>
  tanks.map((tank) => ({
    tankId: tank.id,
    tankName: tank.name,
    aliases: [
      normalizeTankReference(tank.id),
      normalizeTankReference(tank.name),
    ].filter(Boolean),
  }));

const resolveHistoricalCompletedAt = ({ voyage, trip, cargo, matchingEntries }) => {
  const sortedEntries = (matchingEntries || [])
    .map((entry) => entry?.localTime)
    .filter(Boolean)
    .sort((left, right) => getTimestampValue(right) - getTimestampValue(left));

  return (
    sortedEntries[0] ||
    voyage?.completedAt ||
    trip?.updatedAt ||
    cargo?.updatedAt ||
    cargo?.createdAt ||
    null
  );
};

export const isCargoHistoricalForTank = ({ voyage, completedAt }) =>
  Boolean((voyage?.status === "completed" || voyage?.completedAt) && completedAt);

const extractTankCargoOccurrences = ({ voyage, trip, cargo }) => {
  const logGroups = [cargo?.loadingLog, cargo?.dischargingLog].filter(Array.isArray);
  const tankEntries = new Map();

  logGroups.forEach((logEntries) => {
    logEntries.forEach((entry) => {
      const selectedTanks = Array.isArray(entry?.tanksNo) ? entry.tanksNo : [];

      selectedTanks.forEach((tankReference) => {
        const normalizedTank = normalizeTankReference(tankReference);
        if (!normalizedTank) return;

        const existingEntries = tankEntries.get(normalizedTank) || [];
        existingEntries.push(entry);
        tankEntries.set(normalizedTank, existingEntries);
      });
    });
  });

  return Array.from(tankEntries.entries())
    .map(([normalizedTank, matchingEntries]) => {
      const completedAt = resolveHistoricalCompletedAt({
        voyage,
        trip,
        cargo,
        matchingEntries,
      });

      if (
        !isCargoHistoricalForTank({
          voyage,
          trip,
          cargo,
          tankReference: normalizedTank,
          completedAt,
        })
      ) {
        return null;
      }

      return {
        tankReference: normalizedTank,
        cargoId: cargo.id,
        cargoName: cargo.productName || cargo.cargo || "",
        voyageId: voyage.id,
        tripId: trip.id,
        completedAt,
      };
    })
    .filter(Boolean);
};

export const createTankCargoHistoryService = ({
  tankRepository,
  voyageRepository,
  tripRepository,
  tripCargoRepository,
}) => ({
  async getLastCargoesByTank(vesselId, options = {}) {
    const {
      limit = DEFAULT_LIMIT,
      configuredTanks = [],
    } = options;

    const [tankEntities, voyages] = await Promise.all([
      tankRepository?.listActiveByVesselId
        ? tankRepository.listActiveByVesselId(vesselId)
        : [],
      voyageRepository.listByVesselId(vesselId),
    ]);

    const tankRows = (tankEntities?.length
      ? getTankRowsFromEntities(tankEntities)
      : getTankRowsFromFallback(configuredTanks)
    ).sort(sortTanksByName);

    const historicalVoyages = (voyages || []).filter(
      (voyage) => voyage?.status === "completed" || voyage?.completedAt
    );

    const operations = [];

    for (const voyage of historicalVoyages) {
      const trips = await tripRepository.listByVoyageId(voyage.id);

      for (const trip of trips) {
        const cargos = await tripCargoRepository.listByTripId(trip.id);

        cargos.forEach((cargo) => {
          operations.push(
            ...extractTankCargoOccurrences({
              voyage,
              trip,
              cargo,
            })
          );
        });
      }
    }

    return tankRows.map((tank) => {
      const cargoes = operations
        .filter((operation) => tank.aliases.includes(operation.tankReference))
        .sort(compareByNewestCompletedAt)
        .slice(0, limit);

      return {
        tankId: tank.tankId,
        tankName: tank.tankName,
        cargoes,
      };
    });
  },
});
